import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {forkJoin, Observable, Subscription} from 'rxjs';
import {filter, flatMap} from 'rxjs/operators';
import {faInfoCircle, IconDefinition} from "@fortawesome/free-solid-svg-icons";
import {IMenuItem, IPopupMenuConfig} from 'libraries/component/popup-menu/popup-menu.component';
import {IApiPagingOutput} from 'libraries/service/resources/IApiPagingOutput';
import {ISearchStatus} from "app_sasha/shared/interfaces/ISearchStatus";
import {BookingsListComponent} from '../bookings-list/bookings-list.component';
import {Yii} from "libraries/interfaces/Yii";
import {
    PopupMenuPermissionService
} from 'app_sasha/shared/services/popup-menu-permission/popup-menu-permission.service';
import {
    BookingProcessingMenuService
} from 'app_sasha/shared/services/booking-processing-menu/booking-processing-menu.service';
import {
    EXPORT_WORDING,
    IProcessingState,
    PROCESSING_WORDING,
    ProcessingService,
    ProcessingType
} from 'libraries/service/processing/processing.service';
import {IApiExportResponse} from 'libraries/interfaces/IApiExportResponse';
import {
    IGetBookingsCommonResponse,
    IGetBookingsStayResponse,
    IProperty
} from 'libraries/service/resources/booking/interfaces/IGetBookings';
import {ConfirmDialogComponent} from 'libraries/component/dialogs/confirm-dialog/confirm-dialog.component';
import {
    ICreateProcessingResponse,
    IProcessingParameter
} from 'libraries/service/resources/model/processing/ICreateProcessing';
import {BtnStyleTypes} from "libraries/ui_button/btn.component";
import {INotification} from "libraries/interfaces/INotification";
import {BookingResourceService} from "libraries/service/resources/booking/bookingResource.service";
import {BookingSearchParameters} from "app_sasha/shared/models/BookingSearchParameters";
import {IHotelProperty} from "libraries/service/resources/model/search/hotel/IHotelProperty";
import {IAddress} from "libraries/hotel/models/address";
import {
    DictionarySearchResourceService
} from "libraries/service/resources/dictionary-search/dictionary-search-resource.service";
import {IMultiselectOptions} from "libraries/module/formbuilder/fields/multiselect/multiselect.model";
import {BookingType} from "libraries/service/resources/booking/interfaces/get-bookings.enum";
import {IBookingSearchResultConfig} from "libraries/interfaces/IConfigurableTable";
import {ConfigurationResourceService} from "libraries/service/resources/configuration/configurationResource.service";
import { BookingListDomainService } from "libraries/service/booking-list-domain/booking-list-domain.service";

const confirmLaunchSynchronizeDialog: {
    title: string;
    question: string;
    cancel: string;
    accept: string;
} = {
    title: Yii.t('wui-sasha-js', 'Actualisation des dossiers'),
    question: Yii.t('wui-sasha-js', 'Voulez-vous actualiser les dossiers ?'),
    cancel: Yii.t('wui-sasha-js', 'Abandonner'),
    accept: Yii.t('wui-sasha-js', 'Actualiser les dossiers sélectionnés'),
};

class Wording {
    public readonly export: string = Yii.t('wui-shared-js', 'Exporter');

}

@Component({
    selector: 'booking-results-container',
    template: `
        <card>
            <div class="card-header-content">
                <h1 *ngIf="title">{{ title }}</h1>
                <div class="btn-right">
                    <ng-container *permissionIf="exportPermission">
                        <popup-menu *permissionIf = "exportCarbonPermission" [menuConfig]="exportMenuConfig"
                                    (onSelect)="dispatchProcessing($event)"></popup-menu>

                        <btn *ngIf="bookings?.length && isDisplayExport"
                             [label]="wording.export"
                             [type]="exportBtnType"
                             (onClick)="export()"></btn>
                    </ng-container>
                    
                    <ng-container *ngIf="bookingSelection.length > 0">
                        <popup-menu *ngIf="canShowPopUpMenu()"
                                    [menuConfig]="processingMenuConfig"
                                    (onSelect)="dispatchProcessing($event)"
                                    class="e2e-sort-menu booking-selection-menu"></popup-menu>

                    </ng-container>

                </div>
            </div>
            <div class="card-body-content">
                <loader *ngIf="searchStatus.isLoading" [message]="labels.bookingLoading"></loader>
                <notification *ngIf="searchStatus.isError" type="error">
                    <strong>{{ labels.bookingError }}</strong> {{searchStatus.errorMessage}}
                </notification>
                <notification
                        *ngIf="(!bookings || !bookings.length) && !searchStatus.isLoading && !searchStatus.isError"
                        type="info">
                    <fa-icon [icon]="iconInfoCircle"></fa-icon>
                    {{ labels.noResults }}
                </notification>

                <loader *ngIf="processingState.isLoading"
                        [message]="processingLabels.processingLoading"></loader>

                <div *ngIf="bookings?.length > 0 && !processingState.isLoading" class="e2e-list-booking">
                    <configurable-table *ngIf="bookingSearchConfig"
                                        [config]="bookingSearchConfig"
                                        [bookings]="bookings"
                                        [recordset]="recordset"
                                        (onSelect)="getBookingSelection($event)"
                                        (onAction)="dispatchProcessing($event)">
                        <ng-container class="list-body">
                            <ng-container *ngIf="processingState.type !== null">
                                <notification *ngIf="!processingState.isError && processingState.successResponse"
                                              type="success">
                                    <strong>{{ processingLabels[processingState.type + 'Success'] }}</strong> :
                                    <a *ngIf="processingState.successResponse.url"
                                       [href]="processingState.successResponse.url">
                                        {{processingState.successResponse.name}}
                                    </a>
                                </notification>
                                <notification *ngIf="processingState.isError" type="error">
                                    <strong>{{ processingLabels[processingState.type + 'Error'] }}</strong>
                                    <span *ngIf="processingState.errorMessage">{{ processingState.errorMessage }}</span>
                                </notification>
                            </ng-container>
                        </ng-container>
                    </configurable-table>
                    <ng-content select=".booking-results-footer"></ng-content>
                </div>

            </div>
        </card>
    `,
    styles: [`
        card ::ng-deep header.header:not(.booking-list-header) {
            min-height: 61px;
            line-height: 40px;
        }

        .btn-right {
            display: flex;
        }

        notification {
            margin: 12px;
            display: block;
        }

        bookings-list notification {
            margin: 0;
            display: block;
        }
    `]
})

export class BookingResultsContainerComponent implements OnInit, OnDestroy, OnChanges {


    @Input()
    public title: string;


    @Input()
    public bookings: IGetBookingsCommonResponse[];

    @Input()
    public recordset: IApiPagingOutput;

    @Input()
    public searchStatus: ISearchStatus = {
        isLoading: false,
        isError: false,
        errorMessage: null,
    };

    @Input()
    public isDisplayExport: boolean = true;

    @Input()
    public searchParams: BookingSearchParameters;

    @ViewChild('bookingsList')
    public bookingsList: BookingsListComponent;

    public processingMenuConfig: IPopupMenuConfig = {
        buttonLabel: PROCESSING_WORDING.buttonLabel,
        items: [],
    };
    public exportMenuConfig: IPopupMenuConfig = {
        buttonLabel: '',
        items: [],
    };
    public processingState: IProcessingState = {
        type: null,
        isLoading: false,
        isError: false,
    };
    public bookingSelection: IGetBookingsCommonResponse[] = [];

    public readonly exportNotification: INotification;


    public processingLabels: { [key: string]: string } = PROCESSING_WORDING;
    public labels: { [key: string]: string } = {
        header: '',
        noResults: Yii.t('wui-sasha-js', 'Aucun résultat pour votre recherche'),
        bookingLoading: Yii.t('wui-sasha-js', 'Chargement des derniers dossiers'),
        bookingError: Yii.t('wui-sasha-js', 'Le chargement des derniers dossiers a échoué'),
    };

    public iconInfoCircle: IconDefinition = faInfoCircle;

    public wording: Wording = new Wording();

    public exportBtnType: BtnStyleTypes = BtnStyleTypes.actionDo;

    public bookingSearchResultConfig: IBookingSearchResultConfig;

    public readonly exportPermission: string = "sasha.booking.search.export.use";

    public readonly exportCarbonPermission: string = "sasha.booking.search.export.carbon.use";

    public bookingSearchConfig: IBookingSearchResultConfig;

    private treatmentDispatchSubscription: Subscription;


    constructor(
        private readonly bookingResourceService: BookingResourceService,
        private readonly processingService: ProcessingService,
        private readonly popupMenuPermissionService: PopupMenuPermissionService,
        private readonly bookingProcessingMenuService: BookingProcessingMenuService,
        private readonly dialog: MatDialog,
        private readonly router: Router,
        private readonly dictionarySearchResourceService: DictionarySearchResourceService,
        private readonly bookingListDomainService: BookingListDomainService,
    ) {
    }

    public ngOnInit(): void {
        this.getConfigurableTableConfig();

        const permissions = [
            {permission: 'sasha.processing.synchronize.use', processing: ProcessingType.Synchronize},
            {permission: 'sasha.processing.email.use', processing: ProcessingType.SendEmail},
            {permission: 'sasha.processing.sms.use', processing: ProcessingType.SendSms},
            {permission: 'sasha.processing.qualityCheck.use', processing: ProcessingType.QualityCheck},
            {permission: 'sasha.processing.issue.use', processing: ProcessingType.Issue},
            {permission: 'sasha.processing.cancel.use', processing: ProcessingType.Cancel},
            {permission: 'sasha.processing.void.use', processing: ProcessingType.Void},
        ];

        const exportPermissions = [
            {permission: 'sasha.booking.search.export.use', processing: ProcessingType.CarbonExport},
        ];

        this.popupMenuPermissionService.getMenuItem(permissions).subscribe(
            (items: IMenuItem[]) => {
                this.processingMenuConfig = {
                    buttonLabel: PROCESSING_WORDING.buttonLabel,
                    items
                };
            }
        );



        this.popupMenuPermissionService.getMenuItem(exportPermissions, EXPORT_WORDING).subscribe(
            (items: IMenuItem[]) => {
                this.exportMenuConfig = {
                    buttonLabel: '',
                    items
                };
            }
        );
    }

    public ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges) {
            this.changeProcessingState({
                isError: false,
                isLoading: false,
                successResponse: null,
            });
        }

        if (simpleChanges.bookings) {
            const hasHotelBooking: IGetBookingsCommonResponse[] = this.bookings.filter((booking: IGetBookingsCommonResponse): boolean => booking.type && booking.type[0] === BookingType.stay);
            if (hasHotelBooking.length) {
                if (!this.searchStatus.isLoading) {
                    this.changeProcessingState({
                        isError: false,
                        isLoading: true,
                        successResponse: null,
                    });
                }
                // LA+#33375 - directly using get list here because cache wasn't populated sometimes
                forkJoin([
                    this.dictionarySearchResourceService.getStateList(),
                    this.dictionarySearchResourceService.getCountryList()
                ]).subscribe(([states, countries]: [IMultiselectOptions[], IMultiselectOptions[]]) => {
                    // LA+#33375 Formatting countries and states for hotel bookings
                    hasHotelBooking
                        .filter((bookingHotel: IGetBookingsStayResponse): boolean => Boolean(bookingHotel.property && bookingHotel.property.length))
                        .map((bookingHotel: IGetBookingsStayResponse): IProperty[] => bookingHotel.property)
                        .map((properties: IHotelProperty[]): void => {
                            properties.map((property: IHotelProperty): IAddress => property.address)
                                .map((address: IAddress): void => {
                                    if (countries.length) {
                                        if (address?.country && (address.country.iso || address.country.code)) {
                                            const countryFound: IMultiselectOptions = countries
                                                .find((item: IMultiselectOptions): boolean =>
                                                    (item.value as string).toLowerCase() === (address.country.iso || address.country.code).toLowerCase()
                                                );
                                            if (countryFound) {
                                                address.country.name = countryFound.name;
                                            }
                                        }
                                    }

                                    if (address?.state && states) {
                                        const stateFound: IMultiselectOptions = states
                                            .find((item: IMultiselectOptions): boolean =>
                                                item.code && address?.state && (item.code as string).toLowerCase() === (typeof address.state === 'string' ? address.state : address.state.code)?.toLowerCase() &&
                                                item.countryCode && address?.country && (item.countryCode as string).toLowerCase() === (address.country.iso || address.country.code).toLowerCase()
                                            );
                                        if (stateFound) {
                                            address.state = stateFound.name;
                                        }
                                    }
                                });
                        });

                    if (!this.searchStatus.isLoading) {
                        this.changeProcessingState({
                            isError: false,
                            isLoading: false,
                            successResponse: null,
                        });
                    }
                });
            }
        }
    }

    public ngOnDestroy(): void {
        if (this.treatmentDispatchSubscription) {
            this.treatmentDispatchSubscription.unsubscribe();
        }
    }


    public export(isCarbon: boolean = false): void {

        this.changeProcessingState({
            isError: false,
            isLoading: true,
            type: ProcessingType.ExportAll,
        });
        this.bookingResourceService.searchExport(this.searchParams, isCarbon)
            .subscribe({
                next: (successResponse: IApiExportResponse): void => {
                    this.changeProcessingState({
                        isError: false,
                        isLoading: false,
                        type: ProcessingType.ExportAll,
                        successResponse,
                    });
                },
                error: (errorResponse: Error): void => {
                    this.changeProcessingState({
                        isError: true,
                        errorMessage: errorResponse.message,
                        isLoading: false,
                        type: ProcessingType.Synchronize,
                    });
                }
            });
    }

    public getBookingSelection(selection: IGetBookingsCommonResponse[]): void {
        this.bookingSelection = selection;
    }

    /**
     * @method dispatchProcessing
     * @description ProcessingService.dispatchProcessing execution on treatment menu action selection
     */
    public dispatchProcessing(processingName: ProcessingType): void {
        switch (processingName) {
            case 'csvExport':
                this.changeProcessingState({
                    isError: false,
                    isLoading: true,
                    type: processingName,
                });
                this.csvExport();
                break;
            case 'synchronize':
                this.confirmLaunchSynchronize();
                break;
            case 'carbonExport':
                this.export(true);
                break;
            default:
                this.changeProcessingState({
                    isError: false,
                    isLoading: true,
                    type: processingName,
                });
                this.bookingProcessingMenuService.changePage(processingName, this.bookingSelection, this.searchParams);
        }
    }


    public canShowPopUpMenu(): boolean {
        return this.processingMenuConfig.items.length > 0;
    }


    private confirmLaunchSynchronize(): void {
        const confirmDialog: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
            width: '650px',
            data: confirmLaunchSynchronizeDialog,
        });

        confirmDialog.afterClosed()
            .pipe(
                filter((value: string) => value === 'process'),
                flatMap(() => this.processingService.synchronizeBooking(this.bookingSelection))
            )
            .subscribe({
                next: async (response: ICreateProcessingResponse<IProcessingParameter>): Promise<void> => {
                    await this.router.navigate(['/sasha/processing/', response.id]);
                    this.changeProcessingState({
                        isError: false,
                        isLoading: false,
                        type: ProcessingType.Synchronize,
                    });
                },
                error: (error: Error): void => {
                    this.changeProcessingState({
                        isError: true,
                        errorMessage: error.message,
                        isLoading: false,
                        type: ProcessingType.Synchronize,
                    });
                }
            });
    }

    private changeProcessingState(updates: {
        type?: ProcessingType | null;
        isLoading?: boolean;
        isError?: boolean;
        errorMessage?: string;
        successResponse?: IApiExportResponse;
    }): void {
        this.processingState = {
            ...this.processingState,
            ...updates,
        };
    }

    private csvExport(): void {
        this.treatmentDispatchSubscription = this.processingService.exportCsv(this.bookingSelection)
            .subscribe(
                (response: IApiExportResponse) => {
                    this.changeProcessingState({successResponse: response});
                },
                (error: Error) => {
                    this.changeProcessingState({
                        isError: true,
                        errorMessage: ` : ${error.message}`,
                        isLoading: false,
                    });
                },
                () => {
                    this.changeProcessingState({isLoading: false});
                });
    }

    private getConfigurableTableConfig(): void {
        this.bookingListDomainService.getTableConfiguration().subscribe((config: IBookingSearchResultConfig): void => {
            this.bookingSearchConfig = config;
        });
    }
}
